/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useEffect, useRef, useState } from 'react'
import slugify from 'slugify'
import { Tabs } from '@hashicorp/mds-react/components'
import { vercelSplit } from '@web/cms'
import { Layout } from 'components/materia/layout'
import HeadingText from 'components/materia/sub-blocks/heading-text'
import { AnimatedPanel } from 'components/materia/tabs/components/animated-panel'
import { toKebabCase } from 'lib/to-kebab-case'
import { ABMTabsCard } from './card'
import { ABMModalWrapper } from './modal-wrapper'
import { ABMModalButton } from './modal-button'
import { getModalType } from 'app/[locale]/abm/_utils/get-modal-type'
import { cleanZeroWidthChars } from 'app/[locale]/abm/_utils/clean-zero-width-chars'
import type { AbmTabsSectionFieldsFragment } from 'types/dato'
import s from './abm-tabs.module.css'
import sendPosthogEvent from '@web/utils/send-posthog-event'

const ABMTabsSection = ({
	layout,
	eyebrow,
	headline,
	ctas,
	panels,
}: AbmTabsSectionFieldsFragment) => {
	// Clean card name to remove zero width characters for Posthog event tracking
	const cleanedPanels = panels.map((panel) => ({
		...panel,
		cards: panel.cards.map((card) => ({
			...card,
			headline: cleanZeroWidthChars(card.headline),
		})),
	}))

	const [initialSelectedTabIndex, setInitialSelectedTabIndex] = useState<
		number | null
	>(null)
	const tabsRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const hash = window.location.hash.slice(1)
		if (!hash) {
			setInitialSelectedTabIndex(0)
			return
		}

		const activePanel = cleanedPanels.find((panel) =>
			panel.cards.find(
				(card) => slugify(card.headline, { lower: true }) === hash,
			),
		)

		if (!activePanel) {
			setInitialSelectedTabIndex(0)
			return
		}

		const activePanelIndex = cleanedPanels.indexOf(activePanel)
		setInitialSelectedTabIndex(activePanelIndex)
	}, [])

	useEffect(() => {
		const hash = window.location.hash.slice(1)
		if (hash && tabsRef.current) {
			tabsRef.current.scrollIntoView({ behavior: 'smooth' })
		}
	}, [tabsRef])

	const handleCardClick = ({
		cardName,
		href,
	}: {
		cardName: string
		href: string
	}) => {
		const resourceType = getModalType(vercelSplit(href))
		sendPosthogEvent('abm:card_click', {
			resourceType,
			card_name: cardName,
		})
	}

	return (
		<Layout layout={layout}>
			<div className={s.root} ref={tabsRef}>
				<HeadingText eyebrow={eyebrow} heading={headline} ctas={ctas} />
				<Tabs.Provider
					size="large"
					initialSelectedTabIndex={initialSelectedTabIndex}
					onClickTab={(event, tabIndex) => {
						sendPosthogEvent('abm:tab_click', {
							tabName: cleanedPanels[tabIndex]?.name,
						})
					}}
				>
					<Tabs.TabList>
						{cleanedPanels.map((panel, id) => {
							return (
								<Tabs.Tab
									key={`${id}-tab`}
									trackingKey={toKebabCase(panel.name)}
								>
									{panel.name}
								</Tabs.Tab>
							)
						})}
					</Tabs.TabList>
					<div className={s.panels}>
						{cleanedPanels.map((panel, id) => {
							return (
								<AnimatedPanel key={`${id}-panel`} index={id}>
									<ul className={s.cardsList}>
										{panel.cards.map((card, cardId) => {
											const modalType = getModalType(vercelSplit(card.href))

											if (modalType === 'externalLink') {
												return (
													<li key={`${cardId}-card`} className={s.cardItem}>
														<a
															href={card.href}
															target="_blank"
															rel="noopener noreferrer"
															className={s.externalCard}
															onClick={() => {
																handleCardClick({
																	cardName: card.headline,
																	href: card.href,
																})
															}}
														>
															<ABMTabsCard {...card} isPrimary={cardId === 0} />
														</a>
													</li>
												)
											}

											return (
												<li key={`${cardId}-card`} className={s.cardItem}>
													<ABMModalWrapper
														href={card.href}
														modalId={slugify(card.headline, { lower: true })}
													>
														<ABMModalButton
															modalId={slugify(card.headline, {
																lower: true,
															})}
															href={card.href}
															onClick={() => {
																handleCardClick({
																	cardName: card.headline,
																	href: card.href,
																})
															}}
														>
															<ABMTabsCard {...card} isPrimary={cardId === 0} />
														</ABMModalButton>
													</ABMModalWrapper>
												</li>
											)
										})}
									</ul>
								</AnimatedPanel>
							)
						})}
					</div>
				</Tabs.Provider>
			</div>
		</Layout>
	)
}

export { ABMTabsSection }
